import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetArchiveBusinessFile } from '../../../services/loanService';
import { ArchiveHeader, AttentionList, CUSTOMER_FOLDERS } from './archiveShared';

/**
 * One borrower's file: reusable customer documents, a folder per loan, and how complete and current the file is.
 */
const ArchiveBusinessFilePage: React.FC = () => {
  const { customerId = '' } = useParams<{ customerId: string }>();
  const { data, isLoading, isError } = useGetArchiveBusinessFile(customerId, { query: { enabled: !!customerId } });
  const business = data?.business;
  const loans = business?.loans || [];
  const customerDocs = data?.customerDocuments || [];
  const attention = data?.attention || [];
  const customerAlerts = attention.filter(a => !a.loanReference).length;
  const loanDocuments = loans.reduce((sum, l) => sum + (l.documentCount || 0), 0);
  const subfolders = new Set(customerDocs.map(d => d.folder)).size || CUSTOMER_FOLDERS.length;

  if (isLoading) {
    return <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>;
  }
  if (isError || !business) {
    return <div className="container-fluid py-5 text-center text-muted">This borrower's file could not be loaded. <Link to="/documents">Back to all loan files</Link></div>;
  }

  return (
    <>
      <ArchiveHeader
        crumbs={[{ label: 'All loan files', to: '/documents' }, { label: business.name || 'Borrower' }]}
        backTo="/documents"
        title={business.name || 'Borrower'}
        subtitle={<>{business.registrationNumber && <>{business.registrationNumber} · </>}{loans.length} loan file{loans.length === 1 ? '' : 's'} · {business.documentCount} documents retained</>}
      />
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
      <div className="container-fluid">

        <div className="row g-3">
          <div className="col-lg-8">
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Link to={`/documents/files/${customerId}/customer`} className="text-decoration-none text-reset">
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between">
                        <div className="rounded-3 bg-success-subtle text-success-emphasis d-flex align-items-center justify-content-center" style={{ width: 52, height: 52 }}>
                          <i className="fa fa-building-o fs-4"></i>
                        </div>
                        <i className="fa fa-chevron-right text-muted"></i>
                      </div>
                      <h5 className="fw-bold mt-4">Customer documents</h5>
                      <p className="text-muted flex-grow-1">Reusable company, director, representative, banking and financial records.</p>
                      <div className="d-flex justify-content-between border-top pt-2 small">
                        <span className="fw-semibold">{customerDocs.length} documents · {subfolders} subfolders</span>
                        {customerAlerts > 0
                          ? <span className="text-warning-emphasis"><i className="fa fa-exclamation-circle me-1"></i>{customerAlerts} alert{customerAlerts === 1 ? '' : 's'}</span>
                          : <span className="text-success">Current</span>}
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
              <div className="col-md-6">
                <Link to={`/documents/files/${customerId}/loans`} className="text-decoration-none text-reset">
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14, borderLeft: '4px solid #1f7a5a' }}>
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between">
                        <div className="rounded-3 bg-primary-subtle text-primary-emphasis d-flex align-items-center justify-content-center" style={{ width: 52, height: 52 }}>
                          <i className="fa fa-folder-open-o fs-4"></i>
                        </div>
                        <i className="fa fa-chevron-right text-muted"></i>
                      </div>
                      <h5 className="fw-bold mt-4">Loan-specific documents</h5>
                      <p className="text-muted flex-grow-1">A permanent document folder for every loan, separated by reference number.</p>
                      <div className="d-flex justify-content-between border-top pt-2 small">
                        <span className="fw-semibold">{loans.length} loan folder{loans.length === 1 ? '' : 's'}</span>
                        <span className="text-success fw-semibold">{loanDocuments} documents</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
            <div className="card border-0 bg-primary-subtle" style={{ borderRadius: 14 }}>
              <div className="card-body d-flex gap-3">
                <i className="fa fa-folder-o fs-4 text-primary"></i>
                <div>
                  <div className="fw-semibold text-primary-emphasis">One business file, cleanly separated loan histories</div>
                  <div className="text-primary-emphasis small">
                    Customer-level records may be reused. Every application, assessment, signed agreement, disbursement and repayment
                    document remains locked to its own loan reference.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body">
                <h5 className="fw-bold mb-3">File health</h5>
                <div className="d-flex justify-content-between py-1"><span className="text-muted">Total documents</span><strong>{business.documentCount}</strong></div>
                <div className="d-flex justify-content-between py-1"><span className="text-muted">Loan folders</span><strong>{loans.length}</strong></div>
                <div className="d-flex justify-content-between py-1">
                  <span className="text-muted">Open alerts</span>
                  <strong className={attention.length ? 'text-warning-emphasis' : ''}>{attention.length}</strong>
                </div>
                <div className="progress my-2" style={{ height: 8 }}>
                  <div className="progress-bar bg-success" style={{ width: `${data?.percentCurrent ?? 0}%` }}></div>
                </div>
                <small className="text-success">{data?.percentCurrent ?? 0}% of expected documents are current</small>
                <hr />
                <div className="text-uppercase small text-muted fw-semibold mb-2" style={{ letterSpacing: '0.08em' }}>Recent attention items</div>
                {attention.length ? <AttentionList items={attention} limit={6} /> : <p className="text-muted small mb-0">Nothing needs attention.</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default ArchiveBusinessFilePage;
